/* eslint-disable react/jsx-props-no-spreading */
import React, {
  FC,
  useEffect,
  useRef,
  Children,
  cloneElement,
  ReactElement,
} from 'react';
import { useOverlay, useModal, OverlayContainer } from '@react-aria/overlays';
import { FocusScope } from '@react-aria/focus';
import injectSheet from 'react-jss';
import styleSheet from './styles';
import Animations from '../../animations';

interface Props {
  // eslint-disable-next-line react/no-unused-prop-types
  width?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  height?: number;
  classes?: any;
  title?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  titlePosition?: 'center' | 'left' | 'right';
  onClose?: () => void;
  show?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  overrideStyles?: {
    h2MarginBottom?: string;
    h2FontSize?: string;
    scrollPadding?: string;
    cardWrapperPadding?: string;
  };
  children: any;
}

const Modal: FC<Props> = (props: Props) => {
  const { classes, title, onClose, show, children } = props;

  let isAnimatingOut = false;
  const card = useRef();

  const closeModal = () => {
    if (!isAnimatingOut) {
      Animations.modal.out(card, onClose);
    }
    isAnimatingOut = true;
  };

  // Injects closeModal method to children props //
  const renderChildWithInjectedProps = Children.map(
    children,
    (child: ReactElement) => cloneElement(child, { closeModal })
  );

  useEffect(() => {
    if (show) Animations.modal.in(card);
    else Animations.modal.out(card);
  }, [show]);

  // Handles closing of modal by clicking outside or pressing the Escape key.
  const { overlayProps, underlayProps } = useOverlay(
    {
      isOpen: show,
      onClose: closeModal,
      isDismissable: true,
    },
    card
  );
  // Hides content outside the dialog.
  const { modalProps } = useModal();

  return (
    <OverlayContainer>
      <div className={classes.container} {...underlayProps}>
        <FocusScope contain restoreFocus autoFocus>
          <div
            {...overlayProps}
            {...modalProps}
            role="dialog"
            aria-labelledby="dialog-title"
            aria-modal="true"
            ref={card}
            className={classes.card}
            data-testid="modal-dialog"
          >
            <div className={classes.scroll}>
              <h2 id="dialog-title">{title}</h2>
              {renderChildWithInjectedProps}
            </div>
          </div>
        </FocusScope>
      </div>
    </OverlayContainer>
  );
};

Modal.defaultProps = {
  classes: {},
  width: 0,
  height: 0,
  title: '',
  titlePosition: 'center',
  onClose: () => {},
  show: false,
  overrideStyles: {},
};
export default injectSheet(styleSheet)(Modal);
